import { useEffect, useState, RefObject, useCallback } from "react";
import { defaultBreakpoints, getBreakpoint } from "../utils";
import { BreakPoints } from "./tabs.types";
import useResizeObserver from "../utils/useResizeObserver";

type EventTargetElement = React.RefObject<EventTarget> | Window | null;

export const useEventListener = (
  eventName: string,
  handler: EventListenerOrEventListenerObject,
  element: EventTargetElement
): void => {
  useEffect(() => {
    const targetElement =
      element instanceof Window ? element : element?.current;

    if (!targetElement || !("addEventListener" in targetElement)) return;
    // Add event listener
    targetElement.addEventListener(eventName, handler);

    // Remove event listener on cleanup
    return (): void => {
      targetElement.removeEventListener(eventName, handler);
    };
  }, [eventName, element, handler]);
};

// Need to keep the ref of `scrollRectWidth` out of here because
// we need to keep state in the main file to track when the length changes,
// when breakpoints are sm -> md -> lg (nav buttons show) - changes the scroll container width
// this is important to trigger and get those updated values
// to calculate the minX correctly
export const useDetermineMinMax = (
  scrollRectWidth: number,
  tabListEl: RefObject<HTMLElement>,
  isRtl: boolean,
  tabsContainerWidth: number
): {
  minX: number;
} => {
  const [minX, setMinX] = useState(0);

  useEffect(() => {
    // wait until tabsContainerWidth is available and finished (scroll nav buttons are slow to render)
    // otherwise you will get old element width values
    if (tabsContainerWidth) {
      const min = scrollRectWidth - (tabListEl?.current?.clientWidth || 0);

      setMinX(isRtl ? -min : min);
    }
  }, [isRtl, tabsContainerWidth, minX, scrollRectWidth, tabListEl]);

  return { minX };
};

// happens on initial load and resize
export const useUpdateBreakPoint = (
  breakpoints?: BreakPoints,
  tabsWidth?: number
): string | undefined => {
  const updateBreakPoint = useCallback(() => {
    return getBreakpoint(tabsWidth, breakpoints || defaultBreakpoints);
  }, [breakpoints, tabsWidth]);

  const [breakPoint, setBreakPoint] = useState(updateBreakPoint);

  useEffect(() => {
    setBreakPoint(updateBreakPoint);
  }, [breakpoints, tabsWidth, updateBreakPoint]);

  return breakPoint;
};

export const useThrottledResizeObserver = (
  refEl: RefObject<HTMLElement>,
  wait = 0,
  callback?: (size: { width?: number; height?: number }) => void
): { width?: number; height?: number } => {
  const [size, setSize] = useState({});

  useResizeObserver({
    ref: refEl,
    onResize: ({
      width,
      height,
    }: {
      width?: number;
      height?: number;
    }): void => {
      const validWidth = width ?? 0;
      const validHeight = height ?? 0;
      let timeout: NodeJS.Timeout | null = null;
      const throttle = (callback: () => void, delay: number): void => {
        if (timeout) return;
        timeout = setTimeout(() => {
          callback();
          timeout = null;
        }, delay);
      };

      throttle(() => {
        setSize({ width: validWidth, height: validHeight });
        callback && callback({ width: validWidth, height: validHeight });
      }, wait);
    },
  });

  return { ...size };
};

export const useDetermineScrollability = (
  refs: {
    tabListRef: RefObject<HTMLElement>;
    tabListScrollRef: RefObject<HTMLElement>;
  },
  width: number
): boolean => {
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    if (refs.tabListRef && refs.tabListScrollRef) {
      setIsScrollable(
        (refs.tabListRef.current?.clientWidth || 0) >
          (refs.tabListScrollRef.current?.clientWidth || 0)
      );
    }
  }, [refs.tabListRef, refs.tabListScrollRef, width]);

  return isScrollable;
};
